import { type FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibRadio, type KibRadioModelValue } from '@chewy/kib-choices-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSelect } from '@chewy/kib-fields-new-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import {
  KibModal,
  type KibModalControlsAction,
} from '@chewy/kib-overlays-react'
import classNames from 'classnames'

import useTeamMembers from '~/components/common/inputs/useTeamMembers'
import FeatureToggle from '~/constants/featureToggle'
import RoleName from '~/constants/roleNames'
import { fetchMembersForSelectList } from '~/store/actions/members'
import { useMainStaffRoles } from '~/store/hooks/useMainStaffRoles'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getMembersIsFetchingSelectList,
  getMembersSelectList,
} from '~/store/reducers/members'

import '@chewy/kib-overlays-styles/dist/styles/kib-modal.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-choices-styles/dist/styles/kib-radio.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-select.css'
import '@chewy/kib-fields-styles/dist/styles/kib-input-group.css'
import styles from './CancelRxModal.module.scss'

const MAX_TEAM_MEMBERS = 1000

// TODO(CVC-22515): Wire `onConfirmCancel` to public-api / Partners VetCancel flow; map payload to API contract.
// TODO(CVC-22515): Align `CancelRxReasonValue` / submitted `reason` with backend static reason codes when finalized.

export type CancelRxReasonValue =
  | 'therapy_change'
  | 'no_longer_relevant'
  | 'original_product_out_of_stock'

export interface CancelRxConfirmPayload {
  cancellingDvmId: string
  reason: CancelRxReasonValue
}

export interface CancelRxModalProps {
  onClose?: (event?: unknown) => void
  onConfirmCancel?: (payload: CancelRxConfirmPayload) => void
  open: boolean
  prescriptionName?: string
}

const CancelRxModal: FC<CancelRxModalProps> = ({
  onClose,
  onConfirmCancel,
  open,
  prescriptionName,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')
  const rolesList = useMainStaffRoles()
  const dvmIdList = useSelector(getMembersSelectList)
  const isFetchingMembersSelectList = useSelector(
    getMembersIsFetchingSelectList,
  )
  const teamMembers = useTeamMembers(dvmIdList)
  const hasFetchedDvmsForOpenRef = useRef(false)
  const isCancelRxFeatureEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CANCEL_RX),
  )

  const [cancellationReason, setCancellationReason] =
    useState<CancelRxReasonValue | null>(null)
  const [cancellingDvmId, setCancellingDvmId] = useState('')

  useEffect(() => {
    if (open) {
      setCancellationReason(null)
      setCancellingDvmId('')
    } else {
      hasFetchedDvmsForOpenRef.current = false
    }
  }, [open])

  useEffect(() => {
    if (
      !open ||
      !isCancelRxFeatureEnabled ||
      isFetchingMembersSelectList ||
      teamMembers.length > 0 ||
      hasFetchedDvmsForOpenRef.current
    ) {
      return
    }
    const roleIds = [RoleName.Veterinarian, RoleName.Veterinarian2].flatMap(
      (roleName) =>
        rolesList
          .filter((role) => role.name === roleName)
          .map((role) => role.id),
    )
    if (roleIds.length === 0) {
      return
    }
    hasFetchedDvmsForOpenRef.current = true
    dispatch(
      fetchMembersForSelectList({
        from: 0,
        includeInactive: false,
        noRolesInResult: true,
        roleIds,
        shorten: true,
        to: MAX_TEAM_MEMBERS,
      }),
    )
  }, [
    dispatch,
    isCancelRxFeatureEnabled,
    isFetchingMembersSelectList,
    open,
    rolesList,
    teamMembers.length,
  ])

  const canSubmit =
    cancellationReason != null && cancellingDvmId.trim().length > 0

  const handleCancellationReasonChange = (value: KibRadioModelValue) => {
    if (typeof value === 'string') {
      setCancellationReason(value as CancelRxReasonValue)
    }
  }

  const controls = ({ close }: KibModalControlsAction) => (
    <div className={styles.controlsContainer}>
      <KibButtonNew
        disabled={!canSubmit}
        emphasis="primary"
        onClick={() => {
          if (!canSubmit || cancellationReason == null) {
            return
          }
          onConfirmCancel?.({
            cancellingDvmId,
            reason: cancellationReason,
          })
          close()
        }}
      >
        {t('CANCEL_PRESCRIPTION')}
      </KibButtonNew>
      <KibButtonNew emphasis="secondary" onClick={close}>
        {t('KEEP_PRESCRIPTION')}
      </KibButtonNew>
    </div>
  )

  if (!isCancelRxFeatureEnabled) {
    return null
  }

  return (
    <KibModal
      className={styles.modal}
      controls={controls}
      open={open}
      title={t('CANCEL_PRESCRIPTION')}
      onClose={onClose}
    >
      <div className={styles.body}>
        <div className={styles.introBlock}>
          {prescriptionName ? (
            <p className={styles.prescriptionName}>{prescriptionName}</p>
          ) : null}
          <p className={styles.cancelIntro}>{t('CANCEL_RX_INTRO_BODY')}</p>
        </div>
        <KibInputGroup
          className={styles.reasonInputGroup}
          label={
            <>
              {t('CANCEL_RX_SELECT_REASON')}
              <span className="kib-field__required">&nbsp;*</span>
            </>
          }
        >
          <div className={styles.reasonRadios}>
            <KibRadio
              label={t('CANCEL_RX_REASON_THERAPY_CHANGE')}
              name="cancel-rx-reason"
              selectedValue={cancellationReason ?? undefined}
              value="therapy_change"
              onChange={handleCancellationReasonChange}
            />
            <KibRadio
              label={t('CANCEL_RX_REASON_NO_LONGER_RELEVANT')}
              name="cancel-rx-reason"
              selectedValue={cancellationReason ?? undefined}
              value="no_longer_relevant"
              onChange={handleCancellationReasonChange}
            />
            <KibRadio
              label={t('CANCEL_RX_REASON_ORIGINAL_PRODUCT_OUT_OF_STOCK')}
              name="cancel-rx-reason"
              selectedValue={cancellationReason ?? undefined}
              value="original_product_out_of_stock"
              onChange={handleCancellationReasonChange}
            />
          </div>
        </KibInputGroup>
        <div className={styles.dvmSection}>
          <KibSelect
            required
            className={classNames({
              [styles.dvmSelectPlaceholder]: !cancellingDvmId,
            })}
            label={t('CANCEL_RX_CANCELLING_DVM')}
            name="cancellingDvmId"
            value={cancellingDvmId}
            onChange={(event) => {
              setCancellingDvmId(event.target.value)
            }}
          >
            <option disabled hidden value="">
              {t('SELECT_A_DOCTOR')}
            </option>
            {teamMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {`${member.firstName} ${member.lastName}`}
              </option>
            ))}
          </KibSelect>
        </div>
      </div>
    </KibModal>
  )
}

export default CancelRxModal
